<template>
  <section id="top" class="hero">
    <!-- ambient background -->
    <div class="bg-grid" aria-hidden="true"></div>
    <div class="bg-sweep" aria-hidden="true"></div>

    <!-- registration crosshairs (print/blueprint motif) -->
    <div class="reg" aria-hidden="true">
      <span v-for="n in 4" :key="n" class="reg-m">+</span>
    </div>

    <!-- signal HUD: an oscilloscope panel that draws its waveforms, then a cursor sweeps live -->
    <div class="schematic scope" ref="schem" aria-hidden="true">
      <svg viewBox="0 0 320 220" fill="none">
        <!-- panel frame + corner brackets -->
        <path class="ln frame" d="M14 20 H306 V200 H14 Z" />
        <path class="ln bracket" d="M14 40 V20 H34 M286 20 H306 V40 M306 180 V200 H286 M34 200 H14 V180" />
        <!-- grid -->
        <path class="grid" d="M14 65 H306 M14 110 H306 M14 155 H306" />
        <path class="grid" d="M74 20 V200 M146 20 V200 M218 20 V200 M278 20 V200" />
        <!-- chipset overlay -->
        <rect class="core-shell" x="112" y="58" width="96" height="104" rx="10" />
        <rect class="core" x="132" y="78" width="56" height="64" rx="6" />
        <path class="ln trace" d="M112 84 H88 V64 H58" />
        <path class="ln trace" d="M112 136 H76 V156 H46" />
        <path class="ln trace" d="M208 84 H238 V58 H278" />
        <path class="ln trace" d="M208 136 H248 V166 H286" />
        <path class="ln trace" d="M160 58 V38 H194" />
        <path class="ln trace" d="M160 162 V184 H124" />
        <circle class="node" cx="58" cy="64" r="3" />
        <circle class="node" cx="46" cy="156" r="3" />
        <circle class="node" cx="278" cy="58" r="3" />
        <circle class="node" cx="286" cy="166" r="3" />
        <circle class="node" cx="194" cy="38" r="3" />
        <circle class="node" cx="124" cy="184" r="3" />
        <rect v-for="x in [118, 132, 146, 160, 174, 188]" :key="`pt-${x}`" class="pin" :x="x" y="50" width="6" height="8" rx="1" />
        <rect v-for="x in [118, 132, 146, 160, 174, 188]" :key="`pb-${x}`" class="pin" :x="x" y="162" width="6" height="8" rx="1" />
        <rect v-for="y in [64, 80, 96, 112, 128, 144]" :key="`pl-${y}`" class="pin" x="104" :y="y" width="8" height="6" rx="1" />
        <rect v-for="y in [64, 80, 96, 112, 128, 144]" :key="`pr-${y}`" class="pin" x="208" :y="y" width="8" height="6" rx="1" />
        <text class="chip-label mono" x="160" y="101">NXT GEN</text>
        <text class="chip-meta mono" x="160" y="118">BOARD LOGIC ARRAY</text>
        <!-- baseline axis with ticks -->
        <path class="ln axis" d="M14 110 H306" />
        <path class="ln tick" d="M50 106 V114 M110 106 V114 M170 106 V114 M230 106 V114 M290 106 V114" />
        <!-- waveforms (self-drawing) -->
        <path class="ln wave-sine" ref="waveSine"
          d="M14 110 Q41 60 68 110 T122 110 T176 110 T230 110 T284 110 T306 110" />
        <path class="ln wave-pulse"
          d="M14 150 H60 V90 H96 V150 H150 V78 H182 V150 H238 V104 H272 V150 H306" />
        <!-- vertical scan cursor with a dot riding the sine -->
        <g class="cursor" ref="scan">
          <path class="cur-line" d="M0 22 V198" />
          <circle class="cur-dot" ref="curDot" cx="0" cy="110" r="3" />
        </g>
      </svg>
      <!-- live readout -->
      <div class="readout mono" ref="readout">
        <span class="r-lbl">SIG</span>
        <span class="r-val" ref="rVal">48.0 kHz</span>
        <span class="r-ok">LOCK</span>
      </div>
    </div>

    <div class="main">
      <div class="head-wrap">
        <span class="kicker mono" ref="kicker">
          <i></i> <span ref="kickerTxt">Certified Engineers · Enterprise-Grade Service</span>
        </span>

        <h1 class="display statement" ref="statement">
          <span class="l">
            <span class="wd">Precision</span>&nbsp;<span class="wd">repairs,</span>
          </span>
          <span class="l">
            <em class="wd accent" ref="accent" data-text="engineered">engineered</em>&nbsp;<span class="wd">to</span>
          </span>
          <span class="l"><span class="wd">last.</span></span>
        </h1>

        <p class="sub" ref="sub">
          Precision diagnostics, certified technicians and a written service-level
          guarantee — the standard of care your devices deserve, delivered across Bengaluru.
        </p>

        <!-- blueprint spec line -->
        <div class="spec mono" ref="spec" aria-hidden="true">
          <span>Fig. 01</span><i ref="specLine"></i><span>Tolerance-grade repair · Est. Bengaluru</span>
        </div>
      </div>
    </div>

    <!-- marquee ticker -->
    <div class="marquee mono" aria-hidden="true">
      <div class="track">
        <span v-for="n in 2" :key="n">
          Diagnostics · Motherboard · Screen · Battery · Data Recovery · SSD Upgrade · Keyboard ·
        </span>
      </div>
    </div>
  </section>
</template>

<script>
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

export default {
  name: 'HeroSection',
  mounted() {
    const el = this.$el
    const words = el.querySelectorAll('.statement .wd')
    const marks = el.querySelectorAll('.reg-m')
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches

    // kicker decode — mono glyphs resolve left-to-right into the real text
    const kt = this.$refs.kickerTxt
    const finalTxt = kt.textContent
    const glyphs = '#/<>△▢01'
    const scramble = () => {
      let f = 0
      const total = 22
      this._scr = setInterval(() => {
        const p = ++f / total
        kt.textContent = finalTxt.split('').map((c, i) =>
          (c === ' ' || c === '·' || i < p * finalTxt.length)
            ? c : glyphs[(Math.random() * glyphs.length) | 0]).join('')
        if (f >= total) { clearInterval(this._scr); kt.textContent = finalTxt }
      }, 36)
    }

    this.playIntro = () => {
      if (this._played) return
      this._played = true

      if (reduced) {
        gsap.set([this.$refs.kicker, words, this.$refs.sub, this.$refs.spec, marks],
          { opacity: 1, y: 0, yPercent: 0, clearProps: 'filter' })
        gsap.set(this.$refs.specLine, { scaleX: 1 })
        gsap.set(this.$refs.accent, { '--rule': 1 })
        gsap.set(this.$refs.schem, { opacity: 0.9 })
        this.$refs.schem.querySelectorAll('.ln').forEach(l => gsap.set(l, { strokeDashoffset: 0 }))
        return
      }

      const tl = gsap.timeline({ defaults: { ease: 'power4.out' } })

      // 1 · kicker slips in while its text decodes
      tl.fromTo(this.$refs.kicker,
        { opacity: 0, y: -14, letterSpacing: '8px' },
        { opacity: 0.8, y: 0, letterSpacing: '2px', duration: 0.7, ease: 'power3.out' })
        .add(scramble, '<')

      // 2 · word-by-word reveal — rise + rotate + de-blur (creative-director stagger)
      tl.fromTo(words,
        { yPercent: 115, rotateX: -55, filter: 'blur(12px)', opacity: 0, transformOrigin: '50% 100%' },
        {
          yPercent: 0, rotateX: 0, filter: 'blur(0px)', opacity: 1,
          duration: 1.1, stagger: 0.09, clearProps: 'filter,opacity,transform'
        }, '-=0.25')

      // 3 · support line eases up
      tl.fromTo(this.$refs.sub,
        { opacity: 0, y: 22 }, { opacity: 0.82, y: 0, duration: 0.8, ease: 'power3.out' }, '-=0.55')

      // 3b · spec line draws itself, label fades in; crosshairs snap into place
      tl.fromTo(this.$refs.spec,
        { opacity: 0, y: 12 }, { opacity: 0.65, y: 0, duration: 0.6, ease: 'power3.out' }, '-=0.45')
        .fromTo(this.$refs.specLine,
          { scaleX: 0 }, { scaleX: 1, duration: 0.7, ease: 'power3.inOut' }, '<')
        .fromTo(marks,
          { opacity: 0, scale: 0, rotate: -90 },
          { opacity: 0.4, scale: 1, rotate: 0, duration: 0.5, stagger: 0.07, ease: 'back.out(2)' }, '<')

      // 4 · accent — underline draws, then a sheen band sweeps the outline on a gentle loop
      const accent = this.$refs.accent
      tl.to(accent, { '--rule': 1, duration: 0.6, ease: 'power3.inOut' }, '-=0.5')
        .add(() => {
          this._fill = gsap.timeline({ repeat: -1, repeatDelay: 2.4 })
            .fromTo(accent,
              { '--sheen': '130%' },
              { '--sheen': '-30%', duration: 1.4, ease: 'power1.inOut' })
        })

      // 5 · schematic — die & traces self-draw from the top of the intro
      const schem = this.$refs.schem
      const lines = schem.querySelectorAll('.ln')
      const pins = schem.querySelectorAll('.pin')
      const nodes = schem.querySelectorAll('.node')
      lines.forEach((l) => {
        const len = l.getTotalLength()
        gsap.set(l, { strokeDasharray: len, strokeDashoffset: len })
      })
      tl.to(schem, { opacity: 1, duration: 0.5 }, 0.1)
        .to(lines, { strokeDashoffset: 0, duration: 1.4, stagger: 0.05, ease: 'power2.inOut' }, 0.1)
        .fromTo(pins, { opacity: 0, scale: 0.4, transformOrigin: '50% 50%' },
          { opacity: 0.55, scale: 1, duration: 0.5, stagger: 0.012, ease: 'back.out(2)' }, 0.7)
        .fromTo(nodes, { opacity: 0, scale: 0 },
          { opacity: 0.7, scale: 1, duration: 0.4, stagger: 0.08, ease: 'back.out(3)' }, 0.9)
        .fromTo(this.$refs.readout, { opacity: 0, y: 8 },
          { opacity: 0.75, y: 0, duration: 0.5 }, 1.1)
        .add(() => this.startScan(), 1.1)
    }
    window.addEventListener('preloader:done', this.playIntro, { once: true })
    this._introFallback = setTimeout(this.playIntro, 6500)

    if (!reduced) {
      // scroll parallax — headline drifts up, kicker/sub counter-drift for depth
      this._st = gsap.timeline({
        scrollTrigger: { trigger: el, start: 'top top', end: 'bottom top', scrub: 0.6 }
      })
        .to(this.$refs.statement, { yPercent: -18, ease: 'none' }, 0)
        .to(this.$refs.sub, { yPercent: -40, opacity: 0.4, ease: 'none' }, 0)
        .to(this.$refs.kicker, { yPercent: -60, ease: 'none' }, 0)

      // pointer tilt — headline leans toward the cursor (desktop, fine pointers only)
      if (window.matchMedia('(pointer: fine)').matches) {
        const st = this.$refs.statement
        gsap.set(st, { transformPerspective: 900 })
        const rx = gsap.quickTo(st, 'rotationX', { duration: 0.9, ease: 'power3' })
        const ry = gsap.quickTo(st, 'rotationY', { duration: 0.9, ease: 'power3' })
        this._tilt = (e) => {
          const r = el.getBoundingClientRect()
          ry(((e.clientX - r.left) / r.width - 0.5) * 4)
          rx(((e.clientY - r.top) / r.height - 0.5) * -3)
        }
        this._tiltOut = () => { rx(0); ry(0) }
        el.addEventListener('pointermove', this._tilt)
        el.addEventListener('pointerleave', this._tiltOut)
      }
    }

    // pause looping work (sheen, marquee, ambient CSS) while hero is off-screen
    this._io = new IntersectionObserver(([entry]) => {
      const on = entry.isIntersecting
      el.classList.toggle('is-idle', !on)
      if (this._fill) on ? this._fill.play() : this._fill.pause()
      if (this._scan) on ? this._scan.play() : this._scan.pause()
    })
    this._io.observe(el)
  },
  methods: {
    // scan target orbits the die; readout ticks live coordinates in sync
    startScan() {
      const scan = this.$refs.scan
      const rVal = this.$refs.rVal
      if (!scan) return
      const state = { a: 0 }
      const pad = (n) => String(Math.round(n)).padStart(4, '0')
      this._scan = gsap.to(state, {
        a: Math.PI * 2, duration: 9, ease: 'none', repeat: -1,
        onUpdate: () => {
          const r = 100
          const x = 160 + Math.cos(state.a) * r
          const y = 160 + Math.sin(state.a) * r
          gsap.set(scan, { x, y })
          if (rVal) rVal.textContent = pad(x * 6) + ' · ' + pad(y * 6)
        }
      })
    }
  },
  beforeUnmount() {
    clearTimeout(this._introFallback)
    clearInterval(this._scr)
    this._scan && this._scan.kill()
    window.removeEventListener('preloader:done', this.playIntro)
    if (this._tilt) {
      this.$el.removeEventListener('pointermove', this._tilt)
      this.$el.removeEventListener('pointerleave', this._tiltOut)
    }
    this._io && this._io.disconnect()
    this._fill && this._fill.kill()
    this._st && this._st.scrollTrigger && this._st.scrollTrigger.kill()
  }
}
</script>

<style scoped>
.hero {
  min-height: 100vh;
  background: var(--coral);
  overflow: hidden;
  display: flex; flex-direction: column;
  position: relative;
}

/* --- ambient background --- */

.bg-grid {
  position: absolute; inset: 0; z-index: 0; pointer-events: none;
  background-image:
    linear-gradient(rgba(22,22,26,0.05) 1px, transparent 1px),
    linear-gradient(90deg, rgba(22,22,26,0.05) 1px, transparent 1px);
  background-size: 64px 64px;
  mask-image: radial-gradient(120% 100% at 70% 30%, #000 40%, transparent 100%);
  -webkit-mask-image: radial-gradient(120% 100% at 70% 30%, #000 40%, transparent 100%);
  animation: gridDrift 40s linear infinite;
}
@keyframes gridDrift { to { background-position: 64px 64px; } }
.bg-sweep {
  position: absolute; inset: 0; z-index: 0; pointer-events: none;
  background: radial-gradient(55% 55% at 50% 50%, rgba(255,255,255,0.14), transparent 70%);
  mix-blend-mode: soft-light;
  animation: sweep 14s ease-in-out infinite alternate;
}
@keyframes sweep {
  0%   { transform: translate(-18%, -12%) scale(1); }
  100% { transform: translate(20%, 14%) scale(1.25); }
}

/* blueprint schematic (fills the empty right zone) */
.schematic {
  position: absolute; z-index: 1; pointer-events: none; opacity: 0;
  /* pin to the right edge, clear of the headline column and bottom spec line */
  top: clamp(120px, 24vh, 260px); right: clamp(16px, 3vw, 56px);
  width: clamp(240px, 28vw, 400px); aspect-ratio: 1;
}
.schematic svg { width: 100%; height: 100%; overflow: visible; }
.schematic .ln {
  stroke: var(--ink); stroke-width: 1.2; fill: none; opacity: 0.55;
  vector-effect: non-scaling-stroke;
}
.schematic .core-shell { fill: rgba(22,22,26,0.06); stroke: rgba(22,22,26,0.28); stroke-width: 1.1; }
.schematic .core { fill: rgba(22,22,26,0.14); stroke: rgba(22,22,26,0.28); stroke-width: 1.1; opacity: 0.55; }
.schematic .trace { stroke-width: 1; opacity: 0.38; }
.schematic .pin { fill: var(--ink); opacity: 0; }
.schematic .node { fill: var(--ink); opacity: 0; }
.schematic .chip-label,
.schematic .chip-meta {
  fill: var(--ink); text-anchor: middle; opacity: 0.72;
  letter-spacing: 1.8px;
}
.schematic .chip-label { font-size: 9px; }
.schematic .chip-meta { font-size: 5.5px; opacity: 0.46; letter-spacing: 1.1px; }
/* scan target */
.schematic .scan .ring { fill: none; stroke: var(--ink); stroke-width: 1.2; opacity: 0.5; }
.schematic .scan .cross { stroke: var(--ink); stroke-width: 1.2; opacity: 0.75; }
.schematic .scan .dot { fill: var(--ink); opacity: 0.9; }
.schematic .scan .ring { animation: scanPulse 2.4s ease-in-out infinite; transform-box: fill-box; transform-origin: center; }
@keyframes scanPulse { 50% { opacity: 0.15; transform: scale(1.35); } }
/* live readout — HUD label pinned above the die, right-aligned to it */
.readout {
  position: absolute; right: 2%; top: -8%;
  display: inline-flex; align-items: center; gap: 10px; opacity: 0;
  font-size: 11px; letter-spacing: 2px; text-transform: uppercase;
  color: var(--ink); white-space: nowrap;
}
.readout .r-lbl { opacity: 0.5; }
.readout .r-val { opacity: 0.85; }
.readout .r-ok {
  padding: 1px 6px; border: 1px solid rgba(22,22,26,0.4);
  border-radius: 2px; opacity: 0.6; font-size: 9px;
}

/* registration crosshairs */
.reg { position: absolute; inset: 0; z-index: 1; pointer-events: none; }
.reg-m {
  position: absolute; color: var(--ink); opacity: 0;
  font-size: 15px; font-weight: 300; line-height: 1;
}
.reg-m:nth-child(1) { top: 104px; left: clamp(20px, 4vw, 64px); }
.reg-m:nth-child(2) { top: 104px; right: clamp(20px, 4vw, 64px); }
.reg-m:nth-child(3) { bottom: 68px; left: clamp(20px, 4vw, 64px); }
.reg-m:nth-child(4) { bottom: 68px; right: clamp(20px, 4vw, 64px); }

.main {
  flex: 1; z-index: 1;
  display: flex; align-items: center;
  padding: 120px clamp(24px, 5vw, 80px) clamp(28px, 3vw, 48px);
  position: relative; min-height: 0;
}
.head-wrap { max-width: 100%; }

/* kicker */
.kicker {
  display: inline-flex; align-items: center; gap: 10px;
  font-size: 12px; letter-spacing: 2px; text-transform: uppercase;
  color: var(--ink); opacity: 0.8; margin-bottom: clamp(20px, 2.5vw, 34px);
}
.kicker i {
  width: 7px; height: 7px; border-radius: 50%; background: var(--ink);
  display: inline-block; animation: blip 1.8s ease-in-out infinite;
}
@keyframes blip { 50% { transform: scale(0.5); opacity: 0.4; } }

/* statement headline */
.statement {
  margin: 0; color: var(--ink); font-weight: 700;
  font-size: clamp(34px, 4.6vw, 88px); line-height: 1; letter-spacing: -1.5px;
  perspective: 800px;
}
.statement .l {
  display: block; overflow: hidden;
  padding-bottom: 0.22em; margin-bottom: -0.14em;
  white-space: nowrap;
}
.statement .wd { display: inline-block; transform-style: preserve-3d; will-change: transform, filter; }

/* accent word — refined outline that fills once, with a drawn underline (premium editorial) */
.statement em.accent {
  font-style: italic; font-weight: 700;
  position: relative; display: inline-block;
  color: transparent;
  -webkit-text-stroke: 1.6px var(--ink);
  padding-bottom: 0.06em;
}
/* sheen band that sweeps across the outline (metallic "engineered" shine) */
.statement em.accent::before {
  content: attr(data-text);
  position: absolute; left: 0; top: 0;
  -webkit-text-stroke: 1.6px transparent;
  white-space: nowrap; pointer-events: none;
  background: linear-gradient(105deg,
    transparent 42%, var(--ink) 49%, var(--ink) 51%, transparent 58%);
  background-size: 260% 100%;
  background-position-x: var(--sheen, 130%);
  -webkit-background-clip: text; background-clip: text;
  color: transparent;
}
/* thin ink underline rule that draws under the word */
.statement em.accent::after {
  content: ''; position: absolute; left: 0; bottom: 0;
  height: 2px; width: 100%; background: var(--ink);
  transform: scaleX(var(--rule, 0)); transform-origin: left center;
}
.statement em.accent {
  will-change: transform;
  transition: transform 0.5s var(--ease);
}
.statement:hover em.accent { transform: translateY(-4px) rotate(-1deg); }

/* smooth interpolation of the custom props GSAP drives */
@property --sheen { syntax: '<percentage>'; inherits: false; initial-value: 130%; }
@property --rule { syntax: '<number>'; inherits: false; initial-value: 0; }

/* supporting line */
.sub {
  margin: clamp(24px, 3vw, 40px) 0 0; max-width: 34ch;
  font-family: var(--font-display); font-weight: 500;
  font-size: clamp(15px, 1.3vw, 20px); line-height: 1.45;
  color: var(--ink); opacity: 0.82; letter-spacing: -0.3px;
}

/* blueprint spec line */
.spec {
  display: flex; align-items: center; gap: 14px;
  margin-top: clamp(18px, 2vw, 28px);
  font-size: 10px; letter-spacing: 3px; text-transform: uppercase;
  color: var(--ink); opacity: 0; white-space: nowrap;
}
.spec i {
  flex: 0 1 140px; height: 1px; background: var(--ink);
  transform-origin: left center;
}

/* marquee ticker */
.marquee {
  position: relative; z-index: 1;
  border-top: 1px solid rgba(22,22,26,0.25);
  overflow: hidden; white-space: nowrap;
  padding: 12px 0; font-size: 12px; letter-spacing: 2px;
  color: var(--ink); text-transform: uppercase;
}
.marquee .track { display: inline-flex; animation: marquee 32s linear infinite; }
.marquee .track span { padding-right: 8px; }
@keyframes marquee { to { transform: translateX(-50%); } }

/* pause ambient loops when hero is off-screen */
.hero.is-idle .bg-grid,
.hero.is-idle .bg-sweep,
.hero.is-idle .scan .ring,
.hero.is-idle .marquee .track,
.hero.is-idle .kicker i { animation-play-state: paused; }

@media (prefers-reduced-motion: reduce) {
  .bg-grid, .bg-sweep, .marquee .track, .kicker i, .scan .ring { animation: none; }
  .statement em.accent { transition: none; }
}

@media (max-width: 900px) {
  .hero { min-height: 100vh; overflow-x: hidden; }
  .main {
    order: 1; align-items: flex-start; flex: 0 0 auto;
    padding: 104px clamp(20px, 6vw, 40px) clamp(24px, 5vw, 40px);
  }
  .head-wrap { max-width: none; width: 100%; }
  .statement { font-size: clamp(38px, 11vw, 84px); }
  /* let long lines wrap instead of overflowing */
  .statement .l { white-space: normal; }
  .kicker { flex-wrap: wrap; }
  .spec { white-space: normal; flex-wrap: wrap; gap: 8px; }
  .spec i { flex-basis: 48px; }

  /* schematic: reflow into view (below the headline) instead of hiding it */
  .schematic {
    order: 2; position: static; opacity: 1;
    width: min(360px, 82vw); max-width: 100%; margin: -22px auto -4px;
    top: auto; right: auto; pointer-events: auto;
  }
  .marquee { order: 3; }
  /* hide the print crosshairs on mobile — they collide with content */
  .reg { display: none; }
}
</style>
